import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import ExportInquiry from "@/components/sections/ExportInquiry";
import { CONTACT, IMAGES } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { waLink } from "@/lib/contactLinks";
import { MapPin, Phone, Mail, Globe, MessageCircle, ArrowRight } from "lucide-react";

export default function Contact() {
  return (
    <section id="contact" className="relative">
      {/* Final CTA */}
      <div className="relative overflow-hidden">
        <img src={IMAGES.finalCta} alt="Water at dusk — AquaFionix aquaculture" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#030D18]/85" />
        <div className="absolute inset-0 bg-[radial-gradient(100%_80%_at_50%_0%,rgba(0,240,255,0.12),transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-28 text-center">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300">Contact</span>
            <h2 className="font-display mt-4 mx-auto max-w-4xl text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.08] text-white">
              Looking for an Indian <span className="text-gradient-cyan">Makhana Supplier?</span>
            </h2>
            <p className="mt-5 mx-auto max-w-2xl text-base text-slate-300 leading-relaxed">
              Tell us your product requirements, quantity, packaging preference and destination. Our team will
              respond with the relevant product and commercial information.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={() => scrollToId("inquiry")} data-testid="final-request-quote-button" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-7 py-3.5 text-sm font-semibold text-[#1a0f00] hover:-translate-y-0.5 transition-all duration-300">Request a Quote</button>
              <a href={waLink()} target="_blank" rel="noreferrer" data-testid="final-whatsapp-button" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-semibold text-[#052e16] hover:-translate-y-0.5 transition-all duration-300"><MessageCircle className="h-4 w-4" /> WhatsApp Us</a>
              <button onClick={() => scrollToId("inquiry")} data-testid="final-send-inquiry-button" className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur hover:bg-white/10 transition-all duration-300">Send Inquiry <ArrowRight className="h-4 w-4 text-cyan-300" /></button>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Contact info + inquiry form */}
      <div className="py-20 lg:py-28 bg-[#030D18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-10 lg:grid-cols-12 lg:gap-14">
          <Reveal className="lg:col-span-5">
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-white">{CONTACT.company}</h3>
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4 rounded-2xl glass p-5">
                <MapPin className="h-5 w-5 mt-0.5 text-cyan-400 shrink-0" />
                <div><p className="text-sm font-semibold text-white">Registered Office</p><p className="mt-1 text-sm text-slate-400 leading-relaxed">{CONTACT.office}</p></div>
              </div>
              <div className="flex items-start gap-4 rounded-2xl glass p-5">
                <MapPin className="h-5 w-5 mt-0.5 text-emerald-400 shrink-0" />
                <div><p className="text-sm font-semibold text-white">Farm / Project</p><p className="mt-1 text-sm text-slate-400 leading-relaxed">{CONTACT.farm}</p></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <a href={waLink()} target="_blank" rel="noreferrer" className="rounded-2xl glass p-5 hover:border-white/20 transition-colors">
                  <p className="flex items-center gap-2 text-sm font-semibold text-white"><MessageCircle className="h-4 w-4 text-[#25D366]" /> WhatsApp</p>
                  <p className="mt-1 text-sm text-slate-400">{CONTACT.whatsapp}</p>
                </a>
                <div className="rounded-2xl glass p-5">
                  <p className="flex items-center gap-2 text-sm font-semibold text-white"><Phone className="h-4 w-4 text-cyan-400" /> CEO</p>
                  <a href={`tel:${CONTACT.ceoPhone}`} className="mt-1 block text-sm text-slate-400 hover:text-cyan-300 transition-colors">{CONTACT.ceoPhone}</a>
                </div>
              </div>
              <div className="rounded-2xl glass p-5">
                <p className="flex items-center gap-2 text-sm font-semibold text-white"><Mail className="h-4 w-4 text-emerald-400" /> Email</p>
                {CONTACT.emails.map((e) => <a key={e} href={`mailto:${e}`} className="mt-1 block text-sm text-slate-400 hover:text-emerald-300 transition-colors break-all">{e}</a>)}
              </div>
              <a href={`https://${CONTACT.websites[1]}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-2xl glass p-5 text-slate-300 hover:text-white transition-colors">
                <Globe className="h-4 w-4 text-amber-400" /> {CONTACT.websites[1]}
              </a>
              <p className="text-xs text-slate-500 font-mono-tech">CIN: {CONTACT.cin} · PAN: {CONTACT.pan} · TAN: {CONTACT.tan}</p>
            </div>
          </Reveal>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7 }} className="lg:col-span-7">
            <ExportInquiry id="inquiry" heading="Send an Export Inquiry" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
